import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
  :host {
    font-family: "Georgia", serif;
    width: 100%;
    max-width: 1024px;
    height: 100%;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 1fr auto;
    padding: 16px;
    gap: 12px;
    background: radial-gradient(ellipse at center, #121212 0%, #0a0a0a 100%);
    color: #f8f8f8;
    box-sizing: border-box;
  }

  .title {
    text-align: center;
    font-size: 26px;
    font-weight: bold;
    padding: 12px;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    color: #f39c12;
    text-shadow: 0 0 4px rgba(243, 156, 18, 0.4);
    letter-spacing: 1px;
  }

  .header {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;
  }

  .header img {
    height: 313px;
    overflow-x: hidden;
    object-fit: cover;
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.7);
    filter: brightness(0.85);
  }

  .content {
    background-color: rgba(255, 255, 255, 0.03);
    padding: 18px;
    border-radius: 10px;
    line-height: 1.7;
    font-size: 17px;
    font-style: italic;
    box-shadow: inset 0 0 8px rgba(255, 255, 255, 0.02);
    overflow-y: auto;
  }

  .content p {
    margin-bottom: 12px;
    color: #e0e0e0;
  }

  .footer {
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
  }

  .button {
    background-color: #2c3e50;
    border: 1px solid #444;
    padding: 6px 12px;
    border-radius: 6px;
    color: #ecf0f1;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
    transition: background 0.2s, transform 0.2s;
    white-space: nowrap;
    letter-spacing: 0.5px;
  }

  .button:hover {
    background-color: #34495e;
    transform: translateY(-1px);
  }

  .button.active {
    background-color: #f39c12;
    color: #1c1c1c;
    border-color: #f1c40f;
    text-shadow: none;
  }

  .button.object-button {
    background-color: #145374;
    border-color: #0e2a47;
  }

  .button.object-button:hover {
    background-color: #1e5c87;
  }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
          <div class="footer">
            ${this._selectedActionButton
? html`
              <div class="buttons">
                ${this._currentGameState?.objects.map(obj => this.renderGameObjectButton(obj))}
              </div>
            `
: ""}
            <div class="buttons">
              ${this._currentGameState?.actions.map(action => this.renderActionButton(action))}
            </div>
          </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        // Haal de huidige actie op uit de geselecteerde actieknop
        const currentAction: string | undefined = this._selectedActionButton?.name.toLowerCase(); // Zorgt ervoor dat de actie niet hoofdlettergevoelig is

        // Verberg "shadowman" als de actie "pickup" is
        if (currentAction === "oppakken" && button.name.toLowerCase() === "schaduwman") {
            return document.createElement("div");
        }

        // Verberg "shadowman" als de actie "sign" is
        if (currentAction === "teken" && button.name.toLowerCase() === "schaduwman") {
            return document.createElement("div");
        }
        if (currentAction === "teken" && button.name.toLowerCase() === "pen") {
            return document.createElement("div");
        }

        // Verberg "contract" en "pen" als de actie "talk to" is
        if (currentAction === "praat met" && ["contract"].includes(button.name.toLowerCase())) {
            return document.createElement("div");
        }
        if (currentAction === "praat met" && ["pen"].includes(button.name.toLowerCase())) {
            return document.createElement("div");
        }

        if (currentAction === "ga naar" && ["bril", "muur"].includes(button.name.toLowerCase())) {
            return document.createElement("div");
        }
        if (currentAction === "oppakken" && button.name.toLowerCase() === "bord") {
            return document.createElement("div");
        }
        if (currentAction === "oppakken" && button.name.toLowerCase() === "kast") {
            return document.createElement("div");
        }
        if (currentAction === "oppakken" && button.name.toLowerCase() === "muur") {
            return document.createElement("div");
        }

        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
